import {
  FILE_TYPE,
  fileType,
  IAssetManager,
  IFile,
  IGroupInfo,
  IReferralAttachmentInfo,
  MediaDraft,
  UploadedFile,
} from "@home-health-notify/domain";
import { FileObjectType, getFileParams } from "../uploader/FileUtils";
import {
  toAudioFile,
  toDocFile,
  toPhotoFile,
  toVideoFile,
} from "../chats/MediaFiles";
import { uploadToCloudStorage } from "../utils/WebCloudStoargeUploader";
import { v4 as uuid } from "uuid";
import { prepareRequestFromGroupInfo as prepareFromGroupInfo } from "hhn-commons";

// Re-export shared utilities from commons
export {
  defaultPatientInfoData,
  defaultInfo,
  defaultErrors,
  mapUpdatedGroupInfoToPatientInfo,
  mapGroupInfoToIReferralInfo,
  mapGroupInfoToILupaData,
  getRequestData,
  prepareRequestFromGroupInfo,
  validateAddInfo,
  checkDobValidation,
  isValidInfoDetail,
  getFilteredInfoData,
  getLupaData,
  buildPatientGroupPayload,
  validateFormDataLength,
  validatePatientGroupForm,
} from "hhn-commons";
export type { errorsType, ISelectedMembers, IMemberId } from "hhn-commons";

export type EditGroupProps = {
  chatId?: string;
};

// ─── Web-specific functions (platform-dependent file handling) ────────────────

export const prepareRequestFromNewUploads = (
  uploadedFiles: UploadedFile[],
  fileCaptionMap: Map<string, string>,
): IReferralAttachmentInfo[] => {
  const attachmentFiles: IReferralAttachmentInfo[] = [];
  uploadedFiles.forEach(function (uploadFileObj) {
    const fileParams: FileObjectType = getFileParams(uploadFileObj.file);
    const fileSizeInKB = (fileParams.fileSize / 1024).toFixed(2) + " KB";
    const caption: string = fileCaptionMap.get(uploadFileObj.file.name) ?? "";
    attachmentFiles.push({
      url: uploadFileObj.url,
      type: fileParams.assetType,
      attachmentType: fileParams.fileExt,
      size: fileSizeInKB,
      ...(caption && { text: caption }),
    });
  });
  return attachmentFiles;
};

export const populateReferralFiles = async (
  uploadFiles: any,
  groupInfo: IGroupInfo,
  assetManager: IAssetManager,
  fileCaptionMap: Map<string, string>,
): Promise<IReferralAttachmentInfo[]> => {
  let uploadedFiles: UploadedFile[] = [];
  for (const file of uploadFiles) {
    const fileWithSafePath = Object.assign(file, {
      filePath: file.path,
    });
    const fileParams = getFileParams(fileWithSafePath);
    let mediaFile: IFile;
    const type = fileType(fileWithSafePath);
    if (type === FILE_TYPE.IMAGE) {
      mediaFile = toPhotoFile(fileWithSafePath);
    } else if (type === FILE_TYPE.AUDIO) {
      mediaFile = await toAudioFile(fileWithSafePath);
    } else if (type === FILE_TYPE.VIDEO) {
      mediaFile = await toVideoFile(fileWithSafePath);
    } else if (type === FILE_TYPE.DOC) {
      mediaFile = toDocFile(fileWithSafePath);
    } else {
      throw new Error(`Unsupported file: ${fileParams.fileName}`);
    }

    const mediaDraft = new MediaDraft(mediaFile, fileWithSafePath || "");
    const uniqueId = uuid();
    const key = `${uniqueId}.${fileParams.fileExt}`;

    await assetManager.uploadToSignedUrl(
      mediaDraft?.mediaFile,
      key,
      uploadToCloudStorage,
      () => {},
      async uploadSuccessData => {
        uploadedFiles.push({
          file,
          url: uploadSuccessData.url,
        });
      },
      () => {},
    );
  }
  let attachmentFiles: IReferralAttachmentInfo[] = [];
  if (groupInfo) {
    attachmentFiles = prepareFromGroupInfo(
      groupInfo.attachments ?? [],
      fileCaptionMap,
    );
  }
  attachmentFiles.push(
    ...prepareRequestFromNewUploads(uploadedFiles, fileCaptionMap),
  );
  return attachmentFiles;
};
